import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  IconButton,
  Progress,
  Text,
} from '@chakra-ui/react';
import { CustomChevronLeftIcon } from '../../../theme/icons/CustomChevronLeftIcon';
import { CustomSquareDownloadIcon } from '../../../theme/icons/CustomSquareDownloadIcon';
import {
  CONTENT_MEDIA_QUERY,
  MESSAGE_HISTORY_STATUS_CONFIG,
} from './messagingHistory.constants';
import type {
  MessageHistoryRowProps,
  MessageHistoryStatus,
} from './messagingHistory.types';

const MessageHistoryStatusBadge = ({
  status,
}: {
  status: MessageHistoryStatus;
}) => {
  const config = MESSAGE_HISTORY_STATUS_CONFIG[status];

  return (
    <Badge
      bg={config.background}
      color={config.color}
      fontSize="12px"
      justifySelf="center"
      px="12px"
      py="7px"
      textAlign="center"
      whiteSpace="nowrap"
      width="fit-content"
    >
      {config.label}
    </Badge>
  );
};

const MessageHistoryRow = (props: MessageHistoryRowProps) => {
  const { name, content, recipients, status, date, onClick } = props;
  const isError = props.variant === 'error';
  const onAction = props.variant === 'error' ? props.onAction : undefined;
  const isLoading = props.variant === 'loading';
  const progress = isLoading ? props.progress : undefined;
  const hasProgress = progress !== undefined && progress.total > 0;
  const progressValue = hasProgress
    ? Math.max(0, Math.min((progress.processed / progress.total) * 100, 100))
    : 0;

  return (
    <Box
      bg="white"
      borderBottom="1px solid"
      borderBottomColor="subtleBorder"
      borderBottomLeftRadius={isError || isLoading ? 0 : '6px'}
      borderBottomRightRadius={isLoading ? 0 : '6px'}
      borderLeft={isError ? '4px solid' : 'none'}
      borderLeftColor={isError ? 'dangerRed' : undefined}
      borderTopLeftRadius={isError ? 0 : '6px'}
      borderTopRightRadius="6px"
      cursor="pointer"
      onClick={onClick}
      overflow="hidden"
      width="full"
    >
      <Grid
        alignItems="center"
        bg="white"
        columnGap={{ base: 3, md: 6 }}
        px="16px"
        py="20px"
        templateColumns={{
          base: 'minmax(0, 1fr) auto 32px',
          md: 'minmax(140px, 1fr) 80px 130px minmax(150px, 1fr) 32px',
        }}
        sx={{
          [CONTENT_MEDIA_QUERY]: {
            gridTemplateColumns:
              'minmax(140px, 1fr) minmax(220px, 2fr) 80px 130px minmax(150px, 1fr) 32px',
          },
        }}
        textAlign="left"
        width="full"
      >
        <Text color="secondaryDark" fontSize="16px" isTruncated>
          {name}
        </Text>
        <Text
          color="primaryDark"
          display="none"
          fontSize="14px"
          isTruncated
          sx={{ [CONTENT_MEDIA_QUERY]: { display: 'block' } }}
        >
          {content}
        </Text>
        <Text color="secondaryDark" fontSize="16px" textAlign="center">
          {recipients}
        </Text>
        <MessageHistoryStatusBadge status={status} />
        <Text
          color="secondaryDark"
          fontSize="15px"
          textAlign="center"
          whiteSpace="nowrap"
        >
          {date}
        </Text>
        <IconButton
          aria-label={`View ${name}`}
          bg="transparent"
          height="32px"
          icon={
            <CustomChevronLeftIcon
              boxSize="20px"
              color="charcoalGrey"
              transform="rotate(180deg)"
            />
          }
          minWidth="32px"
          variant="unstyled"
          width="32px"
          _hover={{ bg: 'transparent' }}
        />
      </Grid>

      {props.variant === 'loading' && (
        <Box bg="subtleBackground" px={{ base: 4, md: 12 }} py="14px">
          <Text color="grey" fontSize="14px" textAlign="center">
            {props.message}
            {hasProgress
              ? ` [${progress.processed}] of [${progress.total}] messages sent`
              : null}
          </Text>
          {hasProgress && (
            <Progress
              aria-label={`${progress.processed} of ${progress.total} messages sent`}
              value={progressValue}
              height="6px"
              mt="10px"
              borderRadius="full"
              bg="messageProgressTrack"
              sx={{ '& > div': { background: 'messageProgressValue' } }}
            />
          )}
        </Box>
      )}
      {props.variant === 'error' && (
        <Flex
          align={{ base: 'flex-start', md: 'center' }}
          bg="softWhite"
          direction={{ base: 'column', md: 'row' }}
          gap={3}
          justify="space-between"
          px="46px"
          py="7px"
        >
          <Box color="primaryDark" fontSize="14px">
            {props.message}
          </Box>
          {onAction && (
            <Button
              aria-label="Download report"
              color="dangerRed"
              flexShrink={0}
              leftIcon={<CustomSquareDownloadIcon boxSize="16px" />}
              onClick={(event) => {
                event.stopPropagation();
                onAction();
              }}
              px="12px"
              size="sm"
              variant="ghost"
            >
              Download report
            </Button>
          )}
        </Flex>
      )}
    </Box>
  );
};

export default MessageHistoryRow;
